import {objectFromEntries} from '@benchling/js-utils';
import {useMemo} from 'react';

import {FullyAccessibleGrid} from 'constants/SampleConstants';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import useGridContentAccessibility from 'samples/grids/hooks/useGridContentAccessibility';
import {
  TestOrderExistingPlateContentsResult,
  useExistingPlateWellTestOrders,
} from 'samples/plate-maps/integrations/procedures/PlateMapTestOrderUtils';
import {
  INACCESSIBLE_GROUP_ID_SENTINEL,
  PlateMap,
  PlateMapWell,
  ReagentType,
} from 'samples/plate-maps/PlateMapConstants';
import {
  getDefaultMeasurement,
  getDefaultMeasurementValidationErrors,
  getDefaultMeasurementWithErrors,
} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationUtil';
import {formatGridCoordinates} from 'samples/SampleUtil';

import useContainerMeasurements, {ContainerMeasurementsByContainerId} from './useContainerMeasurements';

function getPlateMapFromGrid(
  grid: FullyAccessibleGrid,
  containersData: ContainerMeasurementsByContainerId,
  inaccessibleContainedItemIds: Set<string>,
  testOrderResult: TestOrderExistingPlateContentsResult
): PlateMap {
  const wells: Array<PlateMapWell> = [];
  grid.cells.forEach((row, rowIndex) => {
    row.forEach((cell, columnIndex) => {
      if (cell != null) {
        const quantity = containersData[cell.container.id]?.quantity;
        const schemaTags = objectFromEntries(
          cell.container.tags.map((tag) => [tag.tagSchemaField.apiId, tag])
        );
        wells.push({
          position: {
            row: rowIndex,
            column: columnIndex,
          },
          role: cell.container.role,
          sources: [],
          contents: cell.container.contents.map((content) => {
            const initialConcentration =
              containersData[cell.container.id]?.contents[content.containedItemId]?.concentration;
            const position = formatGridCoordinates(rowIndex, columnIndex);
            return {
              type: ReagentType.WELL_CONTENT,
              id: content.containedItemId,
              groupId: inaccessibleContainedItemIds.has(content.containedItemId)
                ? INACCESSIBLE_GROUP_ID_SENTINEL
                : content.containedItemTagSchemaId,
              initialConcentration: initialConcentration
                ? {value: initialConcentration.value, unit: initialConcentration.unit}
                : getDefaultMeasurement(),
              finalConcentration: {
                measurement: initialConcentration
                  ? {value: initialConcentration.value, unit: initialConcentration.unit}
                  : getDefaultMeasurement(),
                errors: getDefaultMeasurementValidationErrors(),
              },
              testOrders:
                testOrderResult.contentTestOrdersByWellPosition[position]?.[content.containedItemId],
            };
          }),
          schemaTags,
          dataParameterTags: {},
          quantity: quantity ? {value: quantity.value, unit: quantity.unit} : getDefaultMeasurement(),
          finalQuantity: getDefaultMeasurementWithErrors(),
          concentrationLayers: {},
        });
      }
    });
  });
  return {
    id: grid.id,
    size: {
      numRows: grid.storableSchema.height,
      numColumns: grid.storableSchema.width,
    },
    wells,
    testOrderDilutionInfoById: testOrderResult.testOrderInfoById,
  };
}

// Overload function types to enforce that the returned plate map will always be defined if the given grid is defined.
export function usePlateMapFromGrid(
  grid: FullyAccessibleGrid,
  fetchTestOrders?: boolean
): {
  plateMap: PlateMap;
  isPending: boolean;
};
export function usePlateMapFromGrid(
  grid: null,
  fetchTestOrders?: boolean
): {
  plateMap: null;
  isPending: boolean;
};
export function usePlateMapFromGrid(
  grid: FullyAccessibleGrid | null,
  fetchTestOrders?: boolean
): {
  plateMap: PlateMap | null;
  isPending: boolean;
};
/**
 * Convert the given Grid entity to a plate map including any relevant well information.
 *
 * Test orders will only be included if `fetchTestOrders` is passed as true, which should be dependent on the
 * study design context & plate map type.
 */
export function usePlateMapFromGrid(
  grid: FullyAccessibleGrid | null,
  fetchTestOrders: boolean = false
): {
  plateMap: PlateMap | null;
  isPending: boolean;
} {
  const {testOrderResult, isPending: testOrdersArePending} = useExistingPlateWellTestOrders({
    gridId: grid?.id ?? null,
    skip: !fetchTestOrders,
  });
  const {status: contentsQueryStatus, inaccessibleContainedItemIds} = useGridContentAccessibility(grid);
  const containerIds =
    (grid?.cells
      .flatMap((row) => row.map((cell) => cell?.container.id ?? null))
      .filter((maybeId) => Boolean(maybeId)) as Array<string>) ?? [];
  const {isPending: measurementsQueryIsPending, containersData} = useContainerMeasurements(containerIds);
  const isPending =
    measurementsQueryIsPending || contentsQueryStatus === QueryResultType.PENDING || testOrdersArePending;
  const plateMapFromGrid = useMemo(
    () =>
      grid == null
        ? null
        : getPlateMapFromGrid(
            grid,
            containersData ?? {},
            new Set(inaccessibleContainedItemIds),
            testOrderResult
          ),
    [grid, containersData, inaccessibleContainedItemIds, testOrderResult]
  );
  return {
    plateMap: plateMapFromGrid,
    isPending,
  };
}
